import { FC, Fragment, useEffect, useState } from 'react';
import {
  IGeneralData,
  IOption,
  IProductDelivery,
  IproductReferenceDataInCountry,
  IShipmentData,
  SHIPMENT_STATUS,
  SHIPMENT_STATUS_ID
} from '../../../../../../../types/orders';
import TrackingAndDatesSection from '../../../Tab1General/Components/TrackingAndDatesSection/TrackingAndDatesSection';
import { IconEditOrders } from '../../../../../../../assets/DesignSystem/SVGComponents';
import {
  NudosCareTag,
  NudosHoverText,
  NudosSelectButton
} from '../../../../../../../components/NudosComponents';
import { copyClipboard } from '../../../../../../../utils/copyClipboard';
import { CopyIcon } from '../../../../../../../assets/images';
import errorMessage from '../../../../../../../utils/errorMessage';
import DeliveryOrderModal from '../../../../../../../components/DeliveryOrderModal/DeliveryOrderModal';
import DeliveryProcessModal from '../../../../../Components/DeliveryProcessModal/DeliveryProcessModal';
import SerialsModal from '../../../../../Components/SerialsModal/SerialsModal';
import NewEditSkuModal from '../../../../../Components/EditSkuModal/NewEditSkuModal';
import { autoUpdateSku } from '../../../../../../../services/traking.services';
import {
  displayErrorNotification,
  displaySuccessNotification
} from '../../../../../../../utils/displayNudosStandardNotifications';
import { AxiosError } from 'axios';
import { ModalMoveWithPendingConfiguration, PendingSettingsIndicator } from '../../../../../../../components';
import { getBringTotalAdditionalSettings } from '../../../../../../../utils/orders';
import { WarehouseTemporaryStorageButton } from './WarehouseTemporaryStorageButton/WarehouseTemporaryStorageButton';
import { LOCATION_PLACE_TYPE } from '../../../../../../../types/services';
import AddSerialStopInWarehouseModal from '../../../../../Components/AddSerialStopInWarehouseModal/AddSerialStopInWarehouseModal';
import NudosConfirmActionModal from '../../../../../../../components/NudosComponents/NudosConfirmActionModal/NudosConfirmActionModal';
import { myCopyText } from '../../../../../../../utils/myCopyText';
import { formatSpecs } from '../../../../../../../utils/formatSpecs';
import { PRODUCT_BRANDS } from 'types/products';
import useOnlyDevelopers from 'state/setOnlyDevelopers';
import useOrdersState from '../../../../../../../state/useOrdersState';
import BordSelectDropdown from 'components/NudosComponents/BordSelectDropdown/BordSelectDropdown';

import './ShipmentCard.scss';

interface IShipmentCard {
  listOfGroupedShipments: IShipmentData[];
  completeDataForEachGroupOfShipments: IShipmentData;
  productsInShippingGroup: IProductDelivery[];
  isEnabled: boolean;
  index: number;
  orderId: number;
  generalData: IGeneralData;
  updateWithoutLoaders?: () => Promise<void>;
  updateWithLoaders?: () => Promise<void>;
  references: IproductReferenceDataInCountry[];
}

const ShipmentCard: FC<IShipmentCard> = ({
  completeDataForEachGroupOfShipments,
  productsInShippingGroup,
  isEnabled,
  index,
  listOfGroupedShipments,
  orderId,
  generalData,
  updateWithoutLoaders,
  updateWithLoaders,
  references
}: IShipmentCard) => {
  const { onlyDevelopers } = useOnlyDevelopers();
  const { isProductSelected, toggleProductSelection } = useOrdersState();

  const allProductIdsFromShipment = listOfGroupedShipments
    .flatMap(shipment => shipment.products || [])
    .map(product => product.id)
    .filter(id => id !== undefined) as number[];

  const isStockReservation = references
    .map(reference => reference.items)
    .flat()
    .some(item => item?.stockReservation !== null);

  const [idLoad, setIdLoad] = useState<number>();
  const [showMessage, setShowMessage] = useState<boolean>(false);
  const [selectOption, setSelectOption] = useState<IOption>();
  const [showProcessModal, setShowProcessModal] = useState<boolean>(false);
  const [showProcessModalConfirmation, setShowProcessModalConfirmation] = useState<boolean>(false);
  const [isEditedProcesModal, setIsEditedProcessModal] = useState<boolean>(false);
  const [showDeliveryModal, setShowDeliveryModal] = useState<boolean>(false);
  const [editSkuModal, setEditSkuModal] = useState<IProductDelivery>();
  const [loadingSku, setLoadingSku] = useState<boolean>(false);
  const [notFoundSku, setNotFoundSku] = useState<boolean>(false);
  const [showPendingConfigurationModal, setShowPendingConfigurationModal] = useState<boolean>(false);
  const [showSerialsModal, setShowSerialsModal] = useState<IProductDelivery>();
  const [showAddSerialStopInWarehouseModal, setShowAddSerialStopInWarehouseModal] = useState<boolean>(false);

  const productInformationForThisShipment = productsInShippingGroup[0];

  const totalAdditionalSettings = getBringTotalAdditionalSettings(
    productInformationForThisShipment.sku,
    completeDataForEachGroupOfShipments?.shipmentGroupId,
    references || [],
    productsInShippingGroup?.length
  );

  const warehouseDestination = completeDataForEachGroupOfShipments?.place === LOCATION_PLACE_TYPE.NUDOS;
  const lastItemInTheList = index + 1 === listOfGroupedShipments?.length;
  const existPendingConfigurations =
    !!totalAdditionalSettings?.toBeCompleted && totalAdditionalSettings.toBeCompleted > 0;
  const hasUnconfirmedActions = totalAdditionalSettings.withUnconfirmedActions > 0;

  const hasStockSerials = references
    .filter(reference => reference.sku === productInformationForThisShipment?.sku)
    .map(reference => reference.items)
    .flat()
    .some(item => item?.stockReservation?.stockProduct?.serial);

  const existSerials =
    hasStockSerials || completeDataForEachGroupOfShipments?.products?.some(product => !!product?.serial);
  const countryTimeZone = completeDataForEachGroupOfShipments?.country?.timeZone || undefined;
  const hasTemporaryStorage = !!completeDataForEachGroupOfShipments?.warehouseTemporaryStorage?.estimatedEntryDate;

  const singleProduct = {
    name: productInformationForThisShipment?.name,
    pills: productInformationForThisShipment?.pills,
    units: productsInShippingGroup?.length
  };

  const status = completeDataForEachGroupOfShipments?.status;
  const isNotConfirmedStatus = completeDataForEachGroupOfShipments?.statusId !== SHIPMENT_STATUS_ID.CONFIRMED;
  const placeIsNudos = completeDataForEachGroupOfShipments?.place === LOCATION_PLACE_TYPE.NUDOS;

  const allowSkuEditing = [
    SHIPMENT_STATUS.DELIVERY_PROCESS,
    SHIPMENT_STATUS.PENDING,
    SHIPMENT_STATUS.CONFIRMED
  ].includes(status as SHIPMENT_STATUS);

  const optionsList = [
    { name: SHIPMENT_STATUS.PENDING, ShipmentStatusId: SHIPMENT_STATUS_ID.PENDING },
    { name: SHIPMENT_STATUS.CONFIRMED, ShipmentStatusId: SHIPMENT_STATUS_ID.CONFIRMED },
    ...(hasTemporaryStorage
      ? [{ name: SHIPMENT_STATUS.WAREHOUSE_STOP, ShipmentStatusId: SHIPMENT_STATUS_ID.WAREHOUSE_STOP }]
      : []),
    { name: SHIPMENT_STATUS.DELIVERY_PROCESS, ShipmentStatusId: SHIPMENT_STATUS_ID.DELIVERY_PROCESS },
    { name: SHIPMENT_STATUS.DELIVERED, ShipmentStatusId: SHIPMENT_STATUS_ID.DELIVERED },
    { name: SHIPMENT_STATUS.CANCELLED, ShipmentStatusId: SHIPMENT_STATUS_ID.CANCELLED }
  ];

  const isBuyAndHold = completeDataForEachGroupOfShipments?.place === LOCATION_PLACE_TYPE.NUDOS;
  const atWarehouseStatus = selectOption?.name === SHIPMENT_STATUS.WAREHOUSE_STOP;

  const hasPendingConfigMessage = (hasUnconfirmedActions: boolean) => {
    if (hasUnconfirmedActions) {
      return 'El cliente todavía no ha confirmado la(s) configuración(es) de todas las herramientas, ¿seguro que deseas mover a Proceso de entrega?';
    }
    return 'Estás a punto de mover un producto a En proceso de entrega <br /> sin completar las configuraciones adicionales, ¿seguro que deseas continuar?';
  };

  const disabledSelectWhenHasTemporaryStorage = (selectId: number, statusId: number) => {
    if (selectId === SHIPMENT_STATUS_ID.WAREHOUSE_STOP) return { show: true, enabled: true };
    if (selectId === statusId) return { show: false, enabled: false };
    if (selectId < statusId) return { show: false, enabled: false };
    return { show: true, enabled: false };
  };

  const disabledTemporaryStorageToDeliveryProcess = (selectId: number, statusId: number) => {
    if (selectId === SHIPMENT_STATUS_ID.DELIVERY_PROCESS) return { show: true, enabled: true };
    if (selectId === statusId) return { show: false, enabled: false };
    if (selectId < SHIPMENT_STATUS_ID.DELIVERY_PROCESS) return { show: false, enabled: false };
    if (selectId > SHIPMENT_STATUS_ID.DELIVERY_PROCESS) return { show: true, enabled: false };
    return { show: true, enabled: false };
  };

  const diabledSelect = (selectId: number, statusId: number) => {
    const isAppleProduct =
      productInformationForThisShipment.brand?.toLowerCase() === PRODUCT_BRANDS.APPLE.toLowerCase();
    const isShipmentToMexico = completeDataForEachGroupOfShipments?.country?.code === 'mx';
    if (isAppleProduct && isShipmentToMexico && onlyDevelopers) {
      // TODO-JEAN: delete onlyDevelopers condition when the feature is released
      const areMPNRemaining = productsInShippingGroup.some(product => !product.mpnId);
      if (areMPNRemaining) return { show: true, enabled: false };
    }
    if (hasTemporaryStorage && statusId === SHIPMENT_STATUS_ID.CONFIRMED)
      return disabledSelectWhenHasTemporaryStorage(selectId, statusId);
    if (hasTemporaryStorage && statusId === SHIPMENT_STATUS_ID.WAREHOUSE_STOP)
      return disabledTemporaryStorageToDeliveryProcess(selectId, statusId);
    if (selectId === statusId) return { show: false, enabled: false };
    if (selectId === SHIPMENT_STATUS_ID.WAREHOUSE_STOP && statusId > SHIPMENT_STATUS_ID.CONFIRMED)
      return { show: false, enabled: false };
    if (selectId < statusId) return { show: false, enabled: false };
    if (selectId === statusId + 1) return { show: true, enabled: true };
    if (selectId > statusId) return { show: true, enabled: false };
    return { show: false, enabled: false };
  };

  const generateOptions = (shipmentData: IShipmentData) => {
    return optionsList.map((item, index) => {
      const renderDisabledSelectEnabled = diabledSelect(item?.ShipmentStatusId, shipmentData?.statusId || 0).enabled;

      const renderDisabledSelectShow = diabledSelect(item?.ShipmentStatusId, shipmentData?.statusId || 0).show;

      return (
        <Fragment key={`option-${item}-index${index}`}>
          {renderDisabledSelectShow && (
            <div
              className="optionStyle"
              onClick={() => {
                if (renderDisabledSelectEnabled) changeStatusLogic(item);
              }}
            >
              <div className={`optionText ${!renderDisabledSelectEnabled ? 'disabledSelect' : ''} `}>{item?.name} </div>
            </div>
          )}
        </Fragment>
      );
    });
  };

  const changeStatusLogic = async (selectOption: IOption) => {
    if (!selectOption) return;
    const selectedStatus = selectOption.ShipmentStatusId;
    const currentStatus = completeDataForEachGroupOfShipments?.statusId;
    const shipmentId = completeDataForEachGroupOfShipments?.shipmentId;
    const isInDeliveryProcess = selectedStatus === SHIPMENT_STATUS_ID.DELIVERY_PROCESS;
    const isDeliveredTransition =
      selectedStatus === SHIPMENT_STATUS_ID.DELIVERED && currentStatus === SHIPMENT_STATUS_ID.DELIVERY_PROCESS;
    const isValidCurrentStatus =
      currentStatus === SHIPMENT_STATUS_ID.CONFIRMED || currentStatus === SHIPMENT_STATUS_ID.WAREHOUSE_STOP;
    const isWarehouseStop = selectedStatus === SHIPMENT_STATUS_ID.WAREHOUSE_STOP;
    setIdLoad(shipmentId);
    try {
      if (isInDeliveryProcess && isValidCurrentStatus) {
        // mover el shipment de estado 2 (confirmado) o 6 (parada en bodega) a 3 (proceso de entrega)
        if (existPendingConfigurations || hasUnconfirmedActions) {
          setShowPendingConfigurationModal(true);
          return;
        }
        if (atWarehouseStatus) {
          setShowProcessModalConfirmation(true);
          return;
        }
        setShowProcessModal(true);
        return;
      }
      if (isDeliveredTransition) {
        // mover el shipment de estado 3 (proceso de entrega) a 4 (entregado)
        setShowDeliveryModal(true);
        return;
      }
      if (isWarehouseStop) {
        // mover el shipment de estado 2 (confirmado) a 6 (parada en bodega)
        setShowAddSerialStopInWarehouseModal(true);
        return;
      }
    } catch (error) {
      errorMessage();
    } finally {
      setIdLoad(undefined);
    }
  };

  const handleChangeFoundSku = (error: boolean) => setNotFoundSku(error);

  const updateSKU = async (newSKU: string) => {
    setLoadingSku(true);
    const IdsList = productsInShippingGroup.map(item => item?.id);
    const bodySkus = {
      productsIds: IdsList,
      newSku: newSKU?.replaceAll(' ', '')
    };
    try {
      await autoUpdateSku(bodySkus);
      await updateWithoutLoaders?.();
      setEditSkuModal(undefined);
      displaySuccessNotification({
        customJSXMessage: <>Se realizo la solicitud de manera correcta, en un rato debes encontrar el SKU actualizado</>
      });
    } catch (error) {
      const err = error as AxiosError;
      if (err?.response?.data?.message === 'sku not found') {
        handleChangeFoundSku(true);
        displayErrorNotification({
          customJSXMessage: <>Este SKU no existe en el catalogo</>
        });
      }
    } finally {
      setLoadingSku(false);
    }
  };

  const assingStatus = () => {
    const findState = optionsList?.find(
      element => completeDataForEachGroupOfShipments?.statusId === element.ShipmentStatusId
    );
    if (findState) setSelectOption(findState);
  };

  const handleCopyProductInfo = () => {
    copyClipboard({
      text: myCopyText({
        productInfo: {
          ...productInformationForThisShipment,
          unit: references.find(r => r.productName === productInformationForThisShipment.name)?.quantity?.toString()
        }
      })
    });
  };

  const getSerialActionText = (): string => {
    if (hasStockSerials || existSerials) {
      return 'Ver/editar seriales';
    }
    return 'Agregar seriales';
  };

  useEffect(() => {
    if (completeDataForEachGroupOfShipments?.status) assingStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [completeDataForEachGroupOfShipments]);

  return (
    <div className="shipmentCard">
      {showAddSerialStopInWarehouseModal && (
        <AddSerialStopInWarehouseModal
          organizationName={generalData?.orgData?.organizationName || ''}
          orderId={String(orderId)}
          products={productsInShippingGroup}
          setShowAddSerialStopInWarehouseModal={setShowAddSerialStopInWarehouseModal}
          shipmentData={completeDataForEachGroupOfShipments}
          updateWithoutLoaders={updateWithoutLoaders}
          references={references}
        />
      )}
      {showSerialsModal && (
        <SerialsModal
          productInfo={singleProduct}
          singleProduct={showSerialsModal}
          changeShowModal={() => setShowSerialsModal(undefined)}
          updateWithoutLoaders={updateWithoutLoaders}
          products={productsInShippingGroup}
          references={references}
          customDisabledButton={isStockReservation}
        />
      )}
      {editSkuModal && (
        <NewEditSkuModal
          defaultSku={editSkuModal?.sku || ''}
          handleChangeFoundSku={() => handleChangeFoundSku(false)}
          notFoundSku={notFoundSku}
          closeModal={() => {
            handleChangeFoundSku(false);
            setEditSkuModal(undefined);
          }}
          updateSKU={(newSku: string) => updateSKU(newSku)}
          loadingSku={loadingSku}
          productInfo={{ name: editSkuModal?.name, pills: editSkuModal?.pills, units: productsInShippingGroup?.length }}
        />
      )}
      {showProcessModalConfirmation && (
        <NudosConfirmActionModal
          modalText="¿Estás seguro que quieres cambiar de estado? Se actualizara en el WMS para indicar que la herramienta salió de bodega y va hacia el empleado."
          firstButtonText="No, cancelar"
          secondButtonText="Si continuar"
          firstButtonAction={() => setShowProcessModalConfirmation(false)}
          secondButtonAction={() => {
            setShowProcessModal(true);
            setShowProcessModalConfirmation(false);
          }}
          customHeight="250px"
        />
      )}
      {showProcessModal && (
        <DeliveryProcessModal
          changeShowModal={() => setShowProcessModal(false)}
          updateDataTraking={updateWithoutLoaders}
          dataTraking={completeDataForEachGroupOfShipments}
          isEdited={isEditedProcesModal}
          listOfGroupedShipments={listOfGroupedShipments}
          orderId={orderId}
          generalData={generalData}
          receiverName={completeDataForEachGroupOfShipments?.receiverInfo?.receiverName}
          flag={completeDataForEachGroupOfShipments?.country?.flag}
          warehouseTemporaryStorage={completeDataForEachGroupOfShipments?.warehouseTemporaryStorage}
          selectOption={selectOption}
          references={references}
        />
      )}
      {showDeliveryModal && (
        <DeliveryOrderModal
          closeModal={() => setShowDeliveryModal(false)}
          dataTraking={completeDataForEachGroupOfShipments}
          listOfGroupedShipments={listOfGroupedShipments}
          references={references}
          updateDeliveryOrder={updateWithoutLoaders}
          orderId={String(orderId)}
          organizationName={generalData?.orgData?.organizationName || ''}
          receiverName={completeDataForEachGroupOfShipments?.receiverInfo?.receiverName}
          flag={completeDataForEachGroupOfShipments?.country?.flag}
          warehouseTemporaryStorage={completeDataForEachGroupOfShipments?.warehouseTemporaryStorage}
          warehouseDestination={warehouseDestination}
        />
      )}
      <div className="shipmentCardHeader">
        <div className="productTitleContainer">
          {!isNotConfirmedStatus && !placeIsNudos && (
            <NudosSelectButton
              isButtonActive={isProductSelected(productInformationForThisShipment.id)}
              handleClickButton={() =>
                toggleProductSelection(
                  productInformationForThisShipment.id,
                  completeDataForEachGroupOfShipments.shipmentId,
                  completeDataForEachGroupOfShipments.place,
                  allProductIdsFromShipment,
                  completeDataForEachGroupOfShipments,
                  hasTemporaryStorage
                )
              }
              componentSize="xs"
              customClassName="w-3 h-3"
            />
          )}
          <div className="unitsOfTheProductTitle"> {`Producto ${index + 1} de ${listOfGroupedShipments.length} `}</div>
        </div>

        {!isBuyAndHold && (
          <WarehouseTemporaryStorageButton
            shipmentId={completeDataForEachGroupOfShipments?.shipmentId}
            initialValues={completeDataForEachGroupOfShipments?.warehouseTemporaryStorage || {}}
            reloadDataCallback={updateWithLoaders}
            selectOption={selectOption}
            shipmentData={completeDataForEachGroupOfShipments}
            orderId={String(orderId)}
            organizationName={generalData?.orgData?.organizationName || ''}
            receiverName={completeDataForEachGroupOfShipments?.receiverInfo?.receiverName}
            references={references}
          />
        )}
      </div>
      <div className="cardItemsContainer">
        <div className="leftSection">
          <div className="unitsContainer">
            <div className="grayTitle">Und.</div>
            <div className="valueText">{productsInShippingGroup?.length}</div>
          </div>

          <div className="infoProductDetailsContainer">
            <div className="grayTitle">Producto</div>
            <div
              className="textProductDetails"
              style={{ color: productInformationForThisShipment?.isRequestedChange ? '#999999' : '' }}
            >
              <div className="textLimits">{productInformationForThisShipment?.name}</div>
              <NudosCareTag
                nudosCareType={productInformationForThisShipment?.nudosCareName}
                customHeight="18px"
                customFontSize="8px"
              />
              <NudosHoverText
                text={formatSpecs(productInformationForThisShipment?.pills)}
                charactersLimit={30}
                onlyIfTruncated
                customTextHoverClassName="customTextHoverClassName"
              />
              <div className="shipmentCopyIconWrapper" onClick={handleCopyProductInfo}>
                <CopyIcon stroke="#383838" />

                <div className="shipmentCopyIconTooltip">Copiar información del producto</div>
              </div>
            </div>
            <div
              className="skuProduct sizeFontProductDetails"
              style={{ color: productInformationForThisShipment?.isRequestedChange ? '#999999' : '' }}
            >
              <span> SKU Catalogo: {productInformationForThisShipment?.sku}</span>
              {allowSkuEditing && (
                <div
                  className="editAction"
                  style={{ cursor: productInformationForThisShipment?.isRequestedChange ? 'not-allowed' : 'pointer' }}
                  onClick={() => {
                    if (!productInformationForThisShipment?.isRequestedChange) {
                      setEditSkuModal(productInformationForThisShipment);
                    }
                  }}
                >
                  <IconEditOrders />
                </div>
              )}
            </div>
            <PendingSettingsIndicator totalAdditionalSettings={totalAdditionalSettings} />
            <ModalMoveWithPendingConfiguration
              showPendingConfigurationModal={showPendingConfigurationModal}
              setShowPendingConfigurationModal={setShowPendingConfigurationModal}
              setShowProcessModal={setShowProcessModal}
              message={hasPendingConfigMessage(hasUnconfirmedActions)}
            />
            <div
              className="addSerials"
              onClick={() => {
                setShowSerialsModal(productInformationForThisShipment);
              }}
            >
              <span className="addSerialsText">{getSerialActionText()}</span>
              <div
                className="editAction"
                style={{ cursor: productInformationForThisShipment?.isRequestedChange ? 'not-allowed' : 'pointer' }}
              >
                <IconEditOrders />
              </div>
            </div>
          </div>

          <div className="shipmentStatus">
            <div className="grayTitle">Estado</div>
            <div
              className="boxStatus"
              onMouseOut={() => !isEnabled && !idLoad && setShowMessage(true)}
              onMouseLeave={() => setShowMessage(false)}
            >
              <BordSelectDropdown
                isFilled
                isLoading={idLoad === completeDataForEachGroupOfShipments?.shipmentId}
                isDeactivated={!isEnabled || selectOption?.ShipmentStatusId === SHIPMENT_STATUS_ID.DELIVERED}
                selectOptions={generateOptions(completeDataForEachGroupOfShipments)}
                currentValueOrplaceholder={selectOption?.name || 'Pendiente de confirmac..'}
              />
              {showMessage &&
                completeDataForEachGroupOfShipments?.statusId !== SHIPMENT_STATUS_ID.DELIVERED &&
                idLoad !== completeDataForEachGroupOfShipments?.shipmentId && (
                  <div className="floatMessage">
                    Para cambiar el estado debemos recibir el pago. En caso de ser un método de pago manual cambiálo en
                    el Trello cargando el comprobante
                  </div>
                )}
            </div>
          </div>
        </div>
        <div className="rightSection">
          <div className="grayTitle">Información de tracking</div>
          <TrackingAndDatesSection
            shipmentData={completeDataForEachGroupOfShipments as IShipmentData}
            countryTimeZone={countryTimeZone}
            setShowProcessModal={setShowProcessModal}
            setIsEditedProcessModal={setIsEditedProcessModal}
          />
        </div>
      </div>
      {productInformationForThisShipment?.isRequestedChange && <div className="alertSku">En proceso de cambio </div>}
      {!lastItemInTheList && <div className="separationLine" />}
    </div>
  );
};

export default ShipmentCard;
